import { IBorrower, ILoan, ILoanQuote } from '@/app/models/loanQuote'

export type ImportedFieldKey = string

export type BorrowerSide = 'main' | 'co'

export const loanFieldKey = (index: number, field: string): ImportedFieldKey => `loans.${index}.${field}`

export const borrowerFieldKey = (side: BorrowerSide, field: string): ImportedFieldKey => `borrowers.${side}.${field}`

export const BANK_FIELD_KEY: ImportedFieldKey = 'bank.name'
export const PROJECT_PURPOSE_FIELD_KEY: ImportedFieldKey = 'project_purpose'

// Le formulaire d'une ligne de prêt n'expose que ces champs en « nouveau prêt »
export const LOAN_IMPORT_FIELDS = ['type', 'rate', 'amount', 'duration', 'delay', 'stages'] as const

// Le backend stocke les garanties dans le format legacy minuscule ; 'ipt' porte IPT_ITT
const GUARANTEE_STORE_CODES: Record<string, string> = {
    DC: 'dc',
    PTIA: 'ptia',
    IPT_ITT: 'ipt',
    IPP: 'ipp',
    PLUS: 'plus',
}

// Un devis repris (rechargement, retour depuis l'étape 5, devis CRM) ne doit pas se présenter comme vierge
export const quoteHasLoanInput = (quote: ILoanQuote | null): boolean =>
    !!quote?.bank_name ||
    !!(quote?.loans ?? []).some((loan) => !!loan.sum || !!loan.period || !!loan.rate || !!loan.type_loan?.id)

const mergeBorrower = (current?: IBorrower, extracted?: IBorrower): IBorrower | undefined => {
    if (!current) return current
    if (!extracted) return current
    return { ...current, proportion: extracted.proportion, options: extracted.options }
}

// Le profil des étapes 1-2 n'est posté qu'à l'écran 4 : remplacer le quote entier le viderait
export const mergeExtractedQuote = (
    current: ILoanQuote,
    refreshed: ILoanQuote,
    formSignatureDate?: string
): ILoanQuote => {
    // LoanPdfExtractApplyService force la date du jour : sans ça la date saisie avant l'import est perdue
    const signatureDate = formSignatureDate || current.loans?.[0]?.new_date_loan || current.date_contract_begin

    return {
        ...current,
        loans: signatureDate
            ? refreshed.loans?.map((loan) => ({ ...loan, new_date_loan: signatureDate }))
            : refreshed.loans,
        bank_name: refreshed.bank_name,
        date_contract_begin: current.date_contract_begin || refreshed.date_contract_begin,
        type_insurance: refreshed.type_insurance,
        main_borrower: mergeBorrower(current.main_borrower, refreshed.main_borrower) as IBorrower,
        co_borrower: current.co_borrower
            ? mergeBorrower(current.co_borrower, refreshed.co_borrower)
            : current.co_borrower,
    }
}

const isEmpty = (value: unknown): boolean =>
    value === null || value === undefined || value === '' || Number.isNaN(value)

const normalize = (value: unknown): string => {
    if (isEmpty(value)) return ''
    if (typeof value === 'number') return String(value)
    if (typeof value === 'boolean') return value ? '1' : '0'
    if (Array.isArray(value) || typeof value === 'object') return JSON.stringify(value)
    return String(value)
}

const hasChanged = (before: unknown, after: unknown): boolean => {
    if (isEmpty(after)) return false
    return normalize(before) !== normalize(after)
}

const loanValue = (loan: ILoan | undefined, field: string): unknown => {
    if (!loan) return undefined
    switch (field) {
        case 'type':
            return loan.type_loan?.id
        case 'rate':
            return loan.rate
        case 'amount':
            return loan.sum
        case 'duration':
            return loan.period
        // Absent du document, il vaut « 0-0 » : non vide, donc badgé par erreur sur chaque ligne créée
        case 'delay':
            return loan.time_offset || loan.partial_time_offset
                ? `${loan.time_offset ?? 0}-${loan.partial_time_offset ? 1 : 0}`
                : undefined
        case 'stages':
            return loan.palier
        default:
            return undefined
    }
}

const diffBorrower = (
    side: BorrowerSide,
    before: IBorrower | undefined,
    after: IBorrower | undefined,
    imported: Set<ImportedFieldKey>
) => {
    if (!after) return

    if (hasChanged(before?.proportion, after.proportion)) {
        imported.add(borrowerFieldKey(side, 'cover_percentage'))
    }

    const beforeOptions = before?.options ?? []
    const afterOptions = after.options ?? []
    Object.entries(GUARANTEE_STORE_CODES).forEach(([code, storeCode]) => {
        const wasChecked = beforeOptions.includes(storeCode)
        const isChecked = afterOptions.includes(storeCode)
        if (isChecked && !wasChecked) {
            imported.add(borrowerFieldKey(side, `options.${code}`))
        }
    })
}

// L'API ne renvoie pas les champs extraits : on compare le quote avant et après, par position de ligne
export const diffImportedFields = (before: ILoanQuote | null, after: ILoanQuote): Set<ImportedFieldKey> => {
    const imported = new Set<ImportedFieldKey>()

    const beforeBank = before?.bank_name || before?.loans?.[0]?.bank?.name
    const afterBank = after.bank_name || after.loans?.[0]?.bank?.name
    if (hasChanged(beforeBank, afterBank)) {
        imported.add(BANK_FIELD_KEY)
    }

    if (hasChanged(before?.type_insurance?.id, after.type_insurance?.id)) {
        imported.add(PROJECT_PURPOSE_FIELD_KEY)
    }

    const beforeLoans = before?.loans ?? []
    ;(after.loans ?? []).forEach((loan, index) => {
        const previous = beforeLoans[index]
        LOAN_IMPORT_FIELDS.forEach((field) => {
            if (hasChanged(loanValue(previous, field), loanValue(loan, field))) {
                imported.add(loanFieldKey(index, field))
            }
        })
    })

    diffBorrower('main', before?.main_borrower, after.main_borrower, imported)
    diffBorrower('co', before?.co_borrower, after.co_borrower, imported)

    return imported
}
